import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PageLayout from '../../components/layout/PageLayout';
import DataTable from '../../components/ui/DataTable';
import StatusChip from '../../components/ui/StatusChip';
import { notify } from '../../components/feedback/notify';
import { MascaraCpf } from "../../utils/MascaraCpf";
import { MascaraTelefone } from "../../utils/MascaraTelefone";
import { MascaraCnpj } from "../../utils/MascaraCnpj";

const normalize = (text) =>
    String(text || "")
        .toLowerCase()
        .normalize("NFD") // remove acentos
        .replace(/[̀-ͯ]/g, "");

function EmitirNota() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [usuarioSearch, setUsuarioSearch] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usuariosComVencidas, setUsuariosComVencidas] = useState(new Set());

    useEffect(() => {
        const getUsuarios = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarUsuariosEmitirNota`, {
                    headers: {
                        Authorization: token
                    }
                })
                setUsuarios(response.data.usuarios || []);
                setUsuarioSearch(response.data.usuarios || []);
            } catch (error) {
                console.error("Erro ao listar clientes:", error);
                notify.error("Não foi possível carregar a lista de clientes.");
            } finally {
                setLoading(false);
            }
        }

        getUsuarios();
    }, [token])

    // Clientes com fatura vencida no Asaas (percorre todas as páginas da rota).
    useEffect(() => {
        const getVencidas = async () => {
            try {
                const ids = new Set();
                const limite = 200;
                for (let pagina = 1; ; pagina++) {
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/cobrancasADM`, {
                        headers: { Authorization: token },
                        params: { status: 'OVERDUE', pagina, limite }
                    });
                    if (response.data.status !== 200) return;
                    (response.data.cobrancas || []).forEach((c) => ids.add(c.idUsuario));
                    if (pagina * limite >= Number(response.data.total || 0)) break;
                }
                setUsuariosComVencidas(ids);
            } catch (error) {
                console.error("Erro ao buscar faturas vencidas:", error);
            }
        };
        getVencidas();
    }, [token])

    const handleSearchUser = (event) => {
        const query = event.target.value.trim().toLowerCase();

        if (!query) {
            setUsuarioSearch(usuarios);
            return;
        }

        const queryNormalizada = normalize(query);
        const queryDigitos = query.replace(/\D/g, "");

        const filteredUsers = usuarios.filter((usuario) => {
            const campos = [
                usuario?.Nome,
                usuario?.Email,
                usuario?.Cpf,
                usuario?.Cnpj,
                usuario?.RazaoSocial,
                usuario?.Telefone,
            ];

            // Também encontra CPF/CNPJ/telefone digitados com máscara (pontos, barras, traços).
            return campos.some((campo) =>
                normalize(campo).includes(queryNormalizada) ||
                (queryDigitos.length >= 3 && String(campo || "").replace(/\D/g, "").includes(queryDigitos))
            );
        });

        setUsuarioSearch(filteredUsers);
    };

    const columns = [
        { key: 'idUsuario', label: 'ID', width: 72 },
        {
            key: 'cliente',
            label: 'Cliente',
            minWidth: 220,
            render: (u) => (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{u?.Nome || '—'}</Typography>
                        {usuariosComVencidas.has(u?.idUsuario) && <StatusChip label="Fatura vencida" tone="error" />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{u?.Email}</Typography>
                </Box>
            ),
        },
        { key: 'Cpf', label: 'CPF', render: (u) => MascaraCpf(u?.Cpf) || '—' },
        { key: 'Cnpj', label: 'CNPJ', render: (u) => MascaraCnpj(u?.Cnpj) || '—' },
        { key: 'RazaoSocial', label: 'Razão Social', render: (u) => u?.RazaoSocial || '—' },
        { key: 'Telefone', label: 'Telefone', render: (u) => MascaraTelefone(u?.Telefone) || '—' },
        {
            key: 'acoes',
            label: '',
            align: 'right',
            width: 140,
            render: (u) => (
                <Button size="small" variant="outlined" endIcon={<ChevronRightIcon fontSize="small" />} onClick={() => navigate(`/EmitirNota/Perfil/${u?.idUsuario}`)}>
                    Abrir perfil
                </Button>
            ),
        },
    ];

    return (
        <PageLayout
            title="Emitir Nota Fiscal"
            subtitle="Selecione o cliente para acessar o perfil e emitir a NFS-e."
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Emitir Nota' }]}
        >
            <DataTable
                columns={columns}
                rows={usuarioSearch}
                getRowKey={(u, i) => u?.idUsuario ?? i}
                loading={loading}
                emptyMessage={usuarios.length === 0 ? "Nenhum cliente disponível para emissão." : "Nenhum cliente corresponde à busca."}
                toolbar={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Buscar por nome, e-mail, CPF, CNPJ, razão social ou telefone"
                            size="small"
                            sx={{ width: { xs: '100%', md: 480 } }}
                            onChange={(event) => handleSearchUser(event)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {usuarioSearch.length} de {usuarios.length} cliente(s)
                        </Typography>
                    </Box>
                }
            />
        </PageLayout>
    );
}

export default EmitirNota;
